import { useEffect, useMemo } from 'react';
import cm from 'classnames';

import { useAppSelector } from 'store/store-hooks';
import { IntroKeys } from 'components/app/intro/intro-keys';
import { useAnimation } from 'components/_hooks/use-animation';
import { isNull, selectClipData, calculateTimePosition } from 'components/app/_helpers/_app.helper';
import { selectHelpMode } from 'components/app/_mediator/app-data.slice';
import { Clip } from 'components/app/editor/_mediator/editor.slice';

import { useTimeStampLinkedPayload } from './_mediator/use-timestamp-linked-payload';
import { useTimestamp } from './_mediator/use-timestamp';
import { useTsSlider } from './use-ts-slider';

import { Tooltip } from 'components/UI/tooltip/tooltip';
import { TooltipHelp } from 'components/UI/tooltip/tooltip-help';
import styles from './_ts-slider.module.scss';

const CLASS__TRANSITION = 'TsSlider--transition';

interface Props {
  isVisible?: boolean;
  onHoverChange?: (isHovered: boolean) => void;
  highlights?: Clip[];
  activeClipId?: string | null;
}

export const TsSlider = ({ isVisible = true, onHoverChange, highlights = [], activeClipId }: Props) => {
  const beginnerMode = useAppSelector((state) => state.app.beginnerMode);
  const activeClip = useAppSelector((state) => state.editor.activeClip);
  const clipsMap = useAppSelector((state) => state.editor.clipsMap);
  const helpMode = useAppSelector(selectHelpMode);
  const playerDuration = useAppSelector((state) => state.timestamp.currentPlayerDuration);

  const { container, startHandle, endHandle, actions, onDrag, onDragEnd } = useTsSlider();
  const { stopAnimation, runAnimation } = useAnimation(container);

  useTimeStampLinkedPayload();
  useTimestamp();

  useEffect(() => {
    stopAnimation([CLASS__TRANSITION]);
    runAnimation([CLASS__TRANSITION]);
  }, [activeClip]);

  const { startTime, endTime } = useMemo(() => {
    if (!activeClip || !clipsMap) return { startTime: null, endTime: null };
    return selectClipData(activeClip, clipsMap);
  }, [activeClip, clipsMap]);

  // Filter valid highlights (only depends on highlights)
  const validHighlights = useMemo(() => {
    if (!highlights.length) return [];

    return highlights.filter(
      (clip) =>
        (clip.startTime !== null && clip.startTime !== undefined) ||
        (clip.endTime !== null && clip.endTime !== undefined)
    );
  }, [highlights]);

  // Calculate positions for highlight tags (only recalculates when playerDuration changes)
  const highlightTagsWithPositions = useMemo(() => {
    if (!playerDuration || playerDuration === 0 || !validHighlights.length) return [];

    return validHighlights.map((clip) => {
      // Use startTime if available, otherwise fall back to endTime
      const time = clip.startTime !== null && clip.startTime !== undefined ? clip.startTime : clip.endTime!;
      const position = calculateTimePosition(time, playerDuration);
      return {
        clip,
        position,
        isActive: clip.clipId === activeClipId,
      };
    });
  }, [validHighlights, activeClipId, playerDuration]);

  return (
    <div
      className={cm(styles.TsSlider, {
        [styles['TsSlider--hidden']]: !actions || !isVisible,
        [styles['TsSlider--beginnerMode']]: beginnerMode,
      })}
      onMouseEnter={() => onHoverChange?.(true)}
      onMouseLeave={() => onHoverChange?.(false)}
    >
      <div ref={container} className={styles.TsSlider__container} data-intro={IntroKeys.srSeek}>
        <div
          ref={startHandle}
          onPointerDown={actions?.start ? (e) => onDrag(e, 'start') : undefined}
          onPointerUp={actions?.start ? (e) => onDragEnd(e, 'start') : undefined}
          className={cm(styles.TsSlider__handle, styles['TsSlider__handle--start'], {
            [styles['TsSlider__handle--disabled']]: !actions?.start,
            [styles['TsSlider__handle--undefined']]: isNull(startTime),
          })}
        >
          <Tooltip showTooltip={!actions?.start && helpMode} dynamicPosition tip={<TipStart />}>
            <span className={styles.TsSlider__handleEl} />
          </Tooltip>
        </div>
        <div
          ref={endHandle}
          onPointerDown={actions?.end ? (e) => onDrag(e, 'end') : undefined}
          onPointerUp={actions?.end ? (e) => onDragEnd(e, 'end') : undefined}
          className={cm(styles.TsSlider__handle, styles['TsSlider__handle--end'], {
            [styles['TsSlider__handle--disabled']]: !actions?.end,
            [styles['TsSlider__handle--undefined']]: isNull(endTime),
          })}
        >
          <Tooltip showTooltip={!actions?.end && helpMode} dynamicPosition tip={<TipEnd />}>
            <span className={styles.TsSlider__handleEl} />
          </Tooltip>
        </div>
        {/* Highlight tags */}
        {highlightTagsWithPositions.length > 0 && (
          <div className={styles.TsSlider__highlightTags}>
            {highlightTagsWithPositions.map(({ clip, position, isActive }) => (
              <div
                key={clip.clipId}
                className={cm(styles.TsSlider__highlightTag, {
                  [styles['TsSlider__highlightTag--active']]: isActive,
                })}
                style={{ '--position': position } as React.CSSProperties}
              >
                <span className={styles.TsSlider__highlightTagEl} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* C O M P O N E N T S */

const TipStart = () => (
  <TooltipHelp>
    <p>
      Start tag is DISABLED: Both <u>Speed</u> and <u>End tag</u> are defined
    </p>
  </TooltipHelp>
);

const TipEnd = () => (
  <TooltipHelp>
    <p>
      End tag is DISABLED: Both <u>Speed</u> and <u>Start tag</u> are defined
    </p>
  </TooltipHelp>
);
